import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatDate, formatEUR } from "@/lib/constants";
import { PhoneCall, ChevronRight, Search, Inbox, Flame } from "lucide-react";
import {
  SolutionBadge,
  PriorityBadge,
  EstimateStatusBadge,
  QueueStatusBadge,
  TimelineBadge,
  HotLeadBadge,
  QUEUE_LABEL,
  SOLUTION_LABEL,
} from "@/components/EnergyBadges";

const QUEUE_STATUS_OPTIONS = [
  "new",
  "estimate_pending",
  "estimate_created",
  "estimate_sent",
  "send_failed",
  "opened",
  "followup_due",
  "replied",
  "appointment_requested",
];

const SOLUTION_OPTIONS = ["pv", "heat_pump", "pv_heat_pump"];

export default function ConversionQueue() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("alle");
  const [solution, setSolution] = useState("alle");
  const [search, setSearch] = useState("");
  const nav = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (status !== "alle") params.status = status;
    if (solution !== "alle") params.solution_type = solution;
    const r = await api.get("/conversion-queue", { params });
    setItems(r.data.items || []);
    setLoading(false);
  }, [status, solution]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const s = search.toLowerCase();
    return items.filter((it) =>
      `${it.name} ${it.email} ${it.phone}`.toLowerCase().includes(s)
    );
  }, [items, search]);

  return (
    <div className="space-y-6" data-testid="conversion-queue-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1
            className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900"
            style={{ fontFamily: "Manrope, sans-serif" }}
          >
            Conversion Queue
          </h1>
          <p className="mt-1 text-slate-600">
            Priorisierte Nachfass-Liste — sofort melden bei Hot-/Urgent-Leads.
          </p>
        </div>
      </div>

      <Card className="border-slate-200 p-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            data-testid="queue-search"
            placeholder="Suche nach Name, E-Mail, Telefon …"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="queue-status-filter" className="w-[200px]">
            <SelectValue placeholder="Status filtern" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="alle">Alle Status</SelectItem>
            {QUEUE_STATUS_OPTIONS.map((s) => (
              <SelectItem key={s} value={s}>
                {QUEUE_LABEL[s] || s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={solution} onValueChange={setSolution}>
          <SelectTrigger data-testid="queue-solution-filter" className="w-[200px]">
            <SelectValue placeholder="Lösung filtern" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="alle">Alle Lösungen</SelectItem>
            {SOLUTION_OPTIONS.map((s) => (
              <SelectItem key={s} value={s}>
                {SOLUTION_LABEL[s]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Card>

      <Card className="border-slate-200 overflow-hidden">
        {/* Desktop */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr className="text-left">
                <th className="px-6 py-3 font-semibold">Kunde / Lösung</th>
                <th className="px-6 py-3 font-semibold">Priorität / Zeitraum</th>
                <th className="px-6 py-3 font-semibold">Estimate / Queue</th>
                <th className="px-6 py-3 font-semibold">Aktivität</th>
                <th className="px-6 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-10 text-center text-slate-500">
                    Wird geladen …
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-16 text-center">
                    <Inbox className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">Keine Leads in der Conversion Queue.</p>
                  </td>
                </tr>
              ) : (
                filtered.map((it) => (
                  <tr
                    key={it.lead_id}
                    data-testid={`queue-row-${it.lead_id}`}
                    onClick={() => nav(`/leads/${it.lead_id}`)}
                    className="hover:bg-slate-50 transition-colors cursor-pointer"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {it.is_hot && <Flame className="w-4 h-4 text-orange-500" />}
                        <div>
                          <p className="font-medium text-slate-900">{it.name}</p>
                          <div className="flex items-center gap-1.5 flex-wrap mt-1">
                            <SolutionBadge value={it.solution_type} testid={`queue-solution-${it.lead_id}`} />
                            {it.is_hot && <HotLeadBadge reason={it.hot_lead_reason} testid={`queue-hot-${it.lead_id}`} />}
                          </div>
                          <p className="text-xs text-slate-500 mt-1">{it.email || it.phone || "—"}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <PriorityBadge value={it.priority} testid={`queue-priority-${it.lead_id}`} />
                        {it.implementation_timeline && (
                          <TimelineBadge value={it.implementation_timeline} testid={`queue-timeline-${it.lead_id}`} />
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {it.estimate_status && (
                          <EstimateStatusBadge value={it.estimate_status} testid={`queue-estimate-${it.lead_id}`} />
                        )}
                        <QueueStatusBadge value={it.conversion_queue_status} testid={`queue-status-${it.lead_id}`} />
                      </div>
                      {it.next_action && (
                        <p className="text-xs text-slate-500 mt-1">→ {it.next_action}</p>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      <p>Letzter Kontakt: {formatDate(it.last_contact_at)}</p>
                      {it.estimated_value > 0 && (
                        <p className="text-xs text-slate-400">Wert: {formatEUR(it.estimated_value)}</p>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <Button
                        data-testid={`queue-contact-${it.lead_id}`}
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          nav(`/leads/${it.lead_id}`);
                        }}
                        className="bg-orange-600 hover:bg-orange-700 text-white"
                      >
                        <PhoneCall className="w-3.5 h-3.5 mr-1.5" /> Jetzt kontaktieren
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile */}
        <div className="md:hidden divide-y divide-slate-100">
          {loading ? (
            <p className="px-4 py-8 text-center text-slate-500 text-sm">Wird geladen …</p>
          ) : filtered.length === 0 ? (
            <div className="px-4 py-10 text-center">
              <Inbox className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 text-sm">Keine Leads in der Conversion Queue.</p>
            </div>
          ) : (
            filtered.map((it) => (
              <button
                key={it.lead_id}
                data-testid={`queue-row-mobile-${it.lead_id}`}
                onClick={() => nav(`/leads/${it.lead_id}`)}
                className="w-full text-left px-4 py-3 hover:bg-slate-50 active:bg-slate-100"
              >
                <div className="flex items-start justify-between gap-3 mb-1.5">
                  <p className="font-semibold text-slate-900 truncate flex items-center gap-1.5">
                    {it.is_hot && <Flame className="w-4 h-4 text-orange-500" />}
                    {it.name}
                  </p>
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <SolutionBadge value={it.solution_type} />
                  <PriorityBadge value={it.priority} />
                  {it.estimate_status && <EstimateStatusBadge value={it.estimate_status} />}
                  <QueueStatusBadge value={it.conversion_queue_status} />
                </div>
                {it.next_action && (
                  <p className="text-xs text-slate-500 mt-1.5">→ {it.next_action}</p>
                )}
              </button>
            ))
          )}
        </div>
      </Card>
    </div>
  );
}
